/**
 * Token Budget Warning Banner
 * Shows a warning banner when the organization's token budget is at ≥80%.
 * Only visible to owners and billing managers.
 */
import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, Coins } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { tokenApi } from '../../services/api';
import { cn } from '../../lib/utils';

// Roles that should see the budget warning
const SHOW_WARNING_ROLES = new Set(['owner', 'billing_manager', 'admin']);

// Thresholds for different warning states
const THRESHOLD_WARNING = 80;
const THRESHOLD_CRITICAL = 100;

const TokenBudgetBanner = ({ onVisibilityChange }) => {
  const { user } = useAuth();
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user should see the banner
  const shouldShowBanner = SHOW_WARNING_ROLES.has(user?.role);

  useEffect(() => {
    if (!shouldShowBanner) {
      setLoading(false);
      onVisibilityChange?.(false);
      return;
    }

    const fetchBudgetStatus = async () => {
      try {
        const res = await tokenApi.getStatus();
        setBudgetStatus(res.data);
      } catch (err) {
        console.warn('Failed to fetch token budget status:', err);
        setBudgetStatus(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBudgetStatus();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchBudgetStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [shouldShowBanner, onVisibilityChange]);

  // Determine if banner should be visible
  const percentUsed = budgetStatus?.percent_used || 0;
  const isWarning = percentUsed >= THRESHOLD_WARNING && percentUsed < THRESHOLD_CRITICAL;
  const isCritical = percentUsed >= THRESHOLD_CRITICAL;
  const isExceeded = budgetStatus?.hard_stopped === true;
  
  const showBanner = shouldShowBanner && !dismissed && !loading && (isWarning || isCritical || isExceeded);

  // Notify parent of visibility changes
  useEffect(() => {
    onVisibilityChange?.(showBanner);
  }, [showBanner, onVisibilityChange]);

  if (!showBanner) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    onVisibilityChange?.(false);
  };

  // Determine banner style based on severity
  const bannerStyle = isExceeded || isCritical
    ? 'bg-red-600 text-white'
    : 'bg-amber-500 text-amber-950';
  
  const iconColor = isExceeded || isCritical ? 'text-white' : 'text-amber-950';

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  return (
    <div 
      className={cn(
        "fixed top-0 left-0 right-0 z-[100] px-4 py-2.5",
        bannerStyle
      )}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {isExceeded ? (
              <AlertTriangle className={cn("w-5 h-5", iconColor)} />
            ) : (
              <Coins className={cn("w-5 h-5", iconColor)} />
            )}
            <span className="font-medium">
              {isExceeded 
                ? 'Token Budget Exceeded' 
                : isCritical 
                  ? 'Token Budget Critical' 
                  : 'Token Budget Warning'}
            </span>
          </div>
          <span className="text-sm opacity-90">
            {isExceeded ? (
              'AI features are disabled. Add tokens to continue.'
            ) : (
              <>
                {percentUsed.toFixed(0)}% used ({formatNumber(budgetStatus?.used || 0)} of {formatNumber(budgetStatus?.budget || 0)} tokens)
              </>
            )}
          </span>
        </div>
        
        <div className="flex items-center gap-3">
          <a
            href="/settings"
            className={cn(
              "text-sm font-medium px-3 py-1 rounded transition-colors",
              isExceeded || isCritical
                ? "bg-white/20 hover:bg-white/30 text-white"
                : "bg-amber-950/20 hover:bg-amber-950/30 text-amber-950"
            )}
          >
            Add Tokens
          </a>
          
          {!isExceeded && (
            <button
              onClick={handleDismiss}
              className={cn(
                "p-1 rounded hover:bg-black/10 transition-colors",
                iconColor
              )}
              title="Dismiss for this session"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenBudgetBanner;
